import { yupResolver } from "@hookform/resolvers";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Form, Header, Icon, Segment } from "semantic-ui-react";
import * as yup from "yup";
import { ControlledInput } from "../../../../../../../components/ControlledForm";
import { parsedPhoneNumberAsString } from "../../../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { useAuthOTPSend } from "../../../../../../auth/hooks/useAuthOTPSend";
import { useNotifySettingsPublic } from "../../../../../hooks/useNotifySettingsPublic";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { OTPModalData, VerifyOTPModal } from "./modals/VerifyOTPModal";
import { ChangeIdentifierModal, ChangeIdentifierModalData } from "./modals/ChangeIdentifierModal";
import { buildReturnUrl } from "../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { useAuthIssueAnonymousTokenByOTP } from "../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTP";

export function ManageEmailOrPhoneNumberCard({}: {}) {
  const { site } = useParams<{ site: string }>();
  const { addToast } = useToasts();
  const { isConfigured } = useNotifySettingsPublic();
  const { sendAuthOTP, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: (data) => {
      setOTPModalData({
        identifier: data.identifier,
        type: data.type,
      });
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });
  const { issueAnonymousTokenByOTP, isAuthIssueAnonymousTokenLoading } = useAuthIssueAnonymousTokenByOTP({
    onSuccess: () => {
      setOTPModalData(null);
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });
  const [changeIdentifierModalData, setChangeIdentifierModalData] = useState<ChangeIdentifierModalData | null>(null);
  const [otpModalData, setOTPModalData] = useState<OTPModalData | null>(null);
  const [buttonClicked, setButtonClicked] = useState<"Subscribe" | "Manage" | null>(null);

  const { subscriberContactMethods, isLoggedInAnonymously } = useNotifySubscriberContactMethodsForLoggedInUser();
  const subscriberContactMethod = subscriberContactMethods?.at(0);

  const { control, handleSubmit, errors, watch } = useForm<{
    emailOrPhoneNumber: string;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        emailOrPhoneNumber: yup
          .string()
          .required("Email Address or Phone Number is required")
          .test("emailOrPhoneNumber", "Invalid Email Address or Phone Number", (value: string) => {
            return isValidPhoneNumber(value) || isValidEmail(value);
          }),
      }),
    ),
    defaultValues: {
      emailOrPhoneNumber: "",
    },
  });
  const emailOrPhoneNumber = watch("emailOrPhoneNumber");

  const getInputIcon = () => {
    if (!emailOrPhoneNumber) return null;
    if (isValidEmail(emailOrPhoneNumber)) return <Icon name='envelope' color='blue' />;
    if (isValidPhoneNumber(emailOrPhoneNumber)) return <Icon name='phone' color='blue' />;
    if (isProbablyEmail(emailOrPhoneNumber)) return <Icon name='envelope' />;
    if (isProbablyPhoneNumber(emailOrPhoneNumber)) return <Icon name='phone' />;
    return null;
  };

  const onSubmit = async (data: { emailOrPhoneNumber: string }) => {
    if (isValidEmail(data.emailOrPhoneNumber)) {
      await sendAuthOTP({
        identifier: data.emailOrPhoneNumber,
        type: "Email",
        returnUrl: buildReturnUrl("Email", data.emailOrPhoneNumber),
      });
    }
    if (isValidPhoneNumber(data.emailOrPhoneNumber)) {
      await sendAuthOTP({
        identifier: data.emailOrPhoneNumber,
        type: "Phone",
      });
    }
  };

  const subscribeCardHeader = isConfigured ? "Email or Phone Number" : "Email Address";
  const identifierIsEmail = subscriberContactMethod?.type === "Email";

  if (isLoggedInAnonymously) {
    return (
      <>
        <VerifyOTPModal
          data={otpModalData}
          onClose={() => setOTPModalData(null)}
          onChangeIdentifier={() => {
            setOTPModalData(null);
            setChangeIdentifierModalData({
              type: identifierIsEmail ? "Email" : "Phone",
            });
          }}
          verifyOTPMutation={{
            isLoading: isAuthIssueAnonymousTokenLoading,
            mutate: async ({ identifier, type, otp }) => {
              await issueAnonymousTokenByOTP({
                identifier,
                type,
                otp,
              });
            },
          }}
        />
        <ChangeIdentifierModal
          data={changeIdentifierModalData}
          onClose={() => setChangeIdentifierModalData(null)}
          onComplete={(data) => {
            setOTPModalData(data);
          }}
        />
        <Segment className='tw-h-42 tw-m-0 tw-w-full tw-space-y-1 tw-p-5'>
          <Header as='h2' className='tw-m-0 tw-text-lg'>
            <div className='tw-flex tw-gap-2'>
              <Icon className='tw-m-0' name='check circle' color='green' />
              {identifierIsEmail
                ? subscriberContactMethod?.identifier
                : parsedPhoneNumberAsString(subscriberContactMethod?.identifier ?? "")}
            </div>
          </Header>
          <p>
            You{"'"}re managing notifications for this {identifierIsEmail ? "email address" : "phone number"}. For more
            options, sign up for an account.
          </p>
          <div className='tw-flex tw-gap-2 tw-pt-3'>
            <Button
              secondary
              fluid
              className='tw-m-0'
              onClick={() =>
                setChangeIdentifierModalData({
                  type: identifierIsEmail ? "Email" : "Phone",
                })
              }
            >
              {identifierIsEmail ? "Change Email" : "Change Phone Number"}
            </Button>
            <Button
              primary
              fluid
              className='tw-m-0'
              as={Link}
              to={{
                pathname: `/${site}/public/register`,
                search: `?redirect=/${site}/public/notify/subscription-groups${
                  subscriberContactMethod?.type === "Email" ? `&email=${subscriberContactMethod?.identifier}` : ""
                }`,
              }}
            >
              Sign Up
            </Button>
          </div>
        </Segment>
      </>
    );
  }

  return (
    <>
      <VerifyOTPModal
        data={otpModalData}
        onClose={() => setOTPModalData(null)}
        onChangeIdentifier={() => {
          setOTPModalData(null);
          setChangeIdentifierModalData({
            type: identifierIsEmail ? "Email" : "Phone",
          });
        }}
        verifyOTPMutation={{
          isLoading: isAuthIssueAnonymousTokenLoading,
          mutate: async ({ identifier, type, otp }) => {
            await issueAnonymousTokenByOTP({
              identifier,
              type,
              otp,
            });
          },
        }}
      />
      <ChangeIdentifierModal
        data={changeIdentifierModalData}
        onClose={() => setChangeIdentifierModalData(null)}
        onComplete={(data) => {
          setOTPModalData(data);
        }}
      />
      <Segment className='tw-h-42 tw-m-0 tw-w-full tw-space-y-1 tw-p-5'>
        <Header as='h2' className='tw-m-0 tw-text-lg'>
          {subscribeCardHeader}
        </Header>
        <Form className='tw-space-y-1'>
          <Form.Field className='tw-m-0' error={!!errors.emailOrPhoneNumber}>
            <ControlledInput
              control={control}
              errors={errors}
              name='emailOrPhoneNumber'
              icon={getInputIcon()}
              iconPosition={"right"}
              fluid
              autoComplete='email'
            />
          </Form.Field>
          <span className='tw-text-xs'>
            By clicking Subscribe you agree to the{" "}
            <a
              href='https://www.gogovapps.com/terms#sms'
              className='tw-font-bold'
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms of Service
            </a>
          </span>
          <div className='tw-flex tw-gap-2'>
            <Button
              primary
              fluid
              className='tw-m-0'
              loading={isAuthOTPSendLoading && buttonClicked === "Subscribe"}
              onClick={handleSubmit((data) => {
                setButtonClicked("Subscribe");
                onSubmit(data);
              })}
            >
              Subscribe
            </Button>
            <Button
              secondary
              fluid
              className='tw-m-0'
              loading={isAuthOTPSendLoading && buttonClicked === "Manage"}
              onClick={handleSubmit((data) => {
                setButtonClicked("Manage");
                onSubmit(data);
              })}
            >
              Manage
            </Button>
          </div>
        </Form>
      </Segment>
    </>
  );
}

// Contains numbers, +, (, ), -, and spaces
export function isProbablyPhoneNumber(value: string) {
  const phoneNumberRegex = /^[0-9+\s()-]+$/;
  return phoneNumberRegex.test(value);
}

// Contains letters, numbers, @, ., _, -, + and spaces
export function isProbablyEmail(value: string) {
  // If it starts with a + or a number, it is probably a phone number
  if (/^[+0-9]/.test(value)) return false;
  const emailRegex = /^[a-zA-Z0-9@._+-]+$/;
  return emailRegex.test(value);
}

const phoneRegExp = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
export function isValidPhoneNumber(value: string) {
  return yup.string().matches(phoneRegExp).isValidSync(value);
}

export function isValidEmail(value: string) {
  return yup.string().email().isValidSync(value);
}
